import { useEffect, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Upload, Download } from "lucide-react";
import { STATUS_STYLES, fmtDate, downloadFile } from "@/components/governance/govUtils";

const blank = { document_id: "", organisation: "", signatory_name: "", signatory_title: "", signatory_email: "", signed_date: "", expiry_date: "" };

export const GovAcceptances = () => {
  const [rows, setRows] = useState([]);
  const [docs, setDocs] = useState([]);
  const [form, setForm] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/governance/acceptances").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    api.get("/governance/documents").then(({ data }) => setDocs(data.filter((d) => ["approved", "published"].includes(d.status) && d.category === "agreement")));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Attach the signed copy"); return; }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", file);
    setBusy(true);
    try { await api.post("/governance/acceptances/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Signed agreement recorded"); setForm(null); setFile(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const end = async (a) => {
    const reason = window.prompt(`Why is the ${a.doc_type.toUpperCase()} with ${a.organisation} ending?`);
    if (!reason) return;
    try { await api.post(`/governance/acceptances/${a.id}/end`, { status: "terminated", reason }); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="gov-acceptances">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">An agreement only counts as signed with evidence: an uploaded signed copy, or an in-app acceptance by the customer's own super admin.</p>
        {!form && <Button size="sm" className="gap-1" onClick={() => setForm(blank)} disabled={!docs.length} title={docs.length ? "" : "Approve an agreement first"} data-testid="gov-acc-upload-btn"><Upload size={13} /> Record signed copy</Button>}
      </div>
      {form && (
        <form onSubmit={submit} className="bg-white border border-border rounded-md p-4 grid md:grid-cols-3 gap-3" data-testid="gov-acc-form">
          <div><Label className="text-xs">Agreement</Label>
            <select required value={form.document_id} onChange={(e) => setForm({ ...form, document_id: e.target.value })} className="h-9 w-full rounded-md border border-border bg-white px-2 text-sm" data-testid="gov-acc-doc-select">
              <option value="">Choose…</option>{docs.map((d) => <option key={d.id} value={d.id}>{d.title} v{d.version}</option>)}
            </select></div>
          {[["organisation", "Organisation (legal name)"], ["signatory_name", "Signatory name"], ["signatory_title", "Signatory job title"], ["signatory_email", "Signatory email"]].map(([k, l]) => (
            <div key={k}><Label className="text-xs">{l}</Label><Input required={k !== "signatory_email"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`gov-acc-${k}`} /></div>
          ))}
          <div><Label className="text-xs">Date signed</Label><Input required type="date" value={form.signed_date} onChange={(e) => setForm({ ...form, signed_date: e.target.value })} data-testid="gov-acc-signed-date" /></div>
          <div><Label className="text-xs">Expiry (optional)</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} /></div>
          <div className="md:col-span-2"><Label className="text-xs">Signed copy (PDF or image)</Label><Input type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="gov-acc-file" /></div>
          <div className="md:col-span-3 flex gap-2"><Button type="submit" disabled={busy} data-testid="gov-acc-save-btn">{busy ? "Saving…" : "Save"}</Button><Button type="button" variant="ghost" onClick={() => setForm(null)}>Cancel</Button></div>
        </form>
      )}
      <div className="bg-white border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-muted-foreground"><tr>{["Agreement", "Organisation", "Signatory", "Method", "Signed", "Status", ""].map((h) => <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground" data-testid="gov-acc-empty">No signed agreements recorded yet.</td></tr>}
            {rows.map((a) => (
              <tr key={a.id} className="border-t" data-testid={`gov-acc-row-${a.id}`}>
                <td className="px-3 py-2">{a.document_title} <span className="text-xs text-muted-foreground">v{a.document_version}</span></td>
                <td className="px-3 py-2">{a.organisation}</td>
                <td className="px-3 py-2 text-xs">{a.signatory_name}<br /><span className="text-muted-foreground">{a.signatory_title}</span></td>
                <td className="px-3 py-2 text-xs">{a.method === "in_app_click_accept" ? "In-app acceptance" : "Signed copy"}</td>
                <td className="px-3 py-2 text-xs">{fmtDate(a.signed_date || a.created_at)}</td>
                <td className="px-3 py-2"><Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[a.status]}`}>{a.status}</Badge></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {a.evidence_path && <button type="button" onClick={() => downloadFile(`/governance/acceptances/${a.id}/evidence`, "signed-copy")} className="p-1 rounded hover:bg-slate-100" title="Download evidence" data-testid={`gov-acc-evidence-${a.id}`}><Download size={13} /></button>}
                  {a.status === "active" && <button type="button" onClick={() => end(a)} className="ml-1 text-xs text-red-600 hover:underline" data-testid={`gov-acc-end-${a.id}`}>End</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
